import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { findValidForClass, findColorType } from 'lib/uiHelpers';
import { PrimaryActionButton } from 'components/ActionButton';

const CardContentPackage = ({ data = [], buttonText, buttonUrl }) => {
  const navigate = useNavigate();

  const goOurClass = (paramsType) => {
    navigate(`/learn/our-class?class_type=${paramsType}`);
  };

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl h-[380px] flex justify-center items-center">
        <div className="flex flex-col gap-4 items-center text-center">
          <span className="text-lg font-semibold text-kin-text">
            You don’t have any active Class Passes yet
          </span>
          <a
            href={buttonUrl}
            className="bg-primary-main text-white rounded-full py-2 px-4 font-semibold text-sm hover:bg-primary-dark transition">
            {buttonText || 'BOOK A CLASS'}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 pb-20">
      {data.map((item, idx) => {
        const pkg = item.event_package;
        const totalQuota = pkg?.credit_number || 0;
        const remainingQuota = item?.class_quota || 0;
        const progressPercent =
          totalQuota > 0 ? Math.round((remainingQuota / totalQuota) * 100) : 0;

        const colorType = findColorType(pkg?.type?.toLowerCase());

        // Handle unlimited
        const isUnlimited =
          !item?.active_until ||
          item?.active_until === '0001-01-01T00:00:00Z' ||
          (typeof item?.active_until === 'string' && item.active_until.startsWith('0001-01-01'));

        // Check if package is expired
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        let isExpired = false;
        if (pkg?.category === 'class') {
          isExpired = isUnlimited
            ? false
            : item?.active_until
            ? (() => {
                try {
                  const activeUntil = parseISO(item.active_until);
                  activeUntil.setHours(0, 0, 0, 0);
                  return activeUntil < now;
                } catch {
                  return false;
                }
              })()
            : false;
        } else {
          // Untuk selain class, expired jika validity_period_month sudah lewat dari tanggal beli
          if (pkg?.validity_period_month && item?.created_at) {
            try {
              const createdAt = parseISO(item.created_at);
              createdAt.setHours(0, 0, 0, 0);
              const expiredDate = new Date(createdAt);
              expiredDate.setMonth(expiredDate.getMonth() + pkg.validity_period_month);
              expiredDate.setHours(0, 0, 0, 0);
              isExpired = expiredDate < now;
            } catch {
              isExpired = false;
            }
          } else {
            isExpired = false;
          }
        }

        // Hitung expiredDate untuk non-class
        let expiredDateStr = '';
        if (pkg?.category === 'class') {
          expiredDateStr = isUnlimited
            ? 'Unlimited'
            : item?.active_until
            ? format(item.active_until, 'dd MMM yyyy')
            : 'Unlimited';
        } else {
          if (pkg?.validity_period_month && item?.created_at) {
            try {
              const createdAt = parseISO(item.created_at);
              createdAt.setHours(0, 0, 0, 0);
              const expiredDate = new Date(createdAt);
              expiredDate.setMonth(expiredDate.getMonth() + pkg.validity_period_month);
              expiredDate.setHours(0, 0, 0, 0);
              expiredDateStr = format(expiredDate, 'dd MMM yyyy');
            } catch {
              expiredDateStr = '-';
            }
          } else {
            expiredDateStr = 'Unlimited';
          }
        }

        return (
          <div
            key={idx}
            className={`group relative bg-white rounded-3xl p-4 sm:p-6 border transition-all shadow-sm hover:shadow-lg ${
              isExpired ? 'border-gray-200 opacity-60 grayscale' : 'border-gray-100'
            }`}>
            {/* Badge Type */}
            <div
              className={`absolute top-0 right-0 px-3 py-1.5 rounded-bl-2xl rounded-tr-2xl text-white text-xs sm:text-sm font-semibold ${colorType}`}>
              {pkg?.type?.toLowerCase() === 'collaboration' ? pkg?.type : `${pkg?.type} Class`}
            </div>

            {/* Header */}
            <div className="flex flex-col gap-1 mb-3">
              {pkg?.category === 'class' ? (
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 tracking-wide">
                  {pkg?.type === 'Regular' ? 'CLASS PASS' : 'SPECIAL CLASS PASS'}
                </p>
              ) : (
                <p className="text-[10px] sm:text-xs font-medium text-gray-500 tracking-wide uppercase">
                  {pkg?.type || '-'}
                </p>
              )}

              <h3
                className="capitalize text-base sm:text-lg font-semibold text-kin-text leading-snug min-h-[60px] sm:min-h-[74px] flex items-start whitespace-pre-line overflow-hidden line-clamp-3"
                title={pkg?.package_name}>
                {pkg?.package_name || '-'}
              </h3>
            </div>

            <div className="flex justify-end items-end mt-2 text-[10px] sm:text-xs text-kin-text/70">
              <span>
                Buy:{' '}
                <strong>
                  {item?.created_at ? format(item?.created_at, 'dd, MMM yyyy') : 'Unknown'}
                </strong>
              </span>
            </div>

            {/* Info Panel */}
            <div className="mt-3 bg-gradient-to-b from-[#F9FAFB] to-[#F3F4F6] p-3 sm:p-4 rounded-xl border border-gray-100">
              {pkg?.category === 'class' ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-medium text-kin-text">
                      Class Quota
                    </span>
                    <span
                      className={`text-xs sm:text-sm font-semibold ${
                        isExpired
                          ? 'text-red-500'
                          : remainingQuota === 0
                          ? 'text-gray-400'
                          : 'text-primary-main'
                      }`}>
                      {remainingQuota}/{totalQuota} Left
                    </span>
                  </div>
                  <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        isExpired
                          ? 'bg-red-400'
                          : remainingQuota === 0
                          ? 'bg-gray-400'
                          : 'bg-primary-main'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs sm:text-sm font-medium text-kin-text">Credit</span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs sm:text-sm font-semibold bg-primary-main/10 text-primary-main">
                    {totalQuota} Sessions
                  </span>
                </div>
              )}

              {/* Expiry Info */}
              <div className="flex flex-col sm:flex-row justify-between gap-1 sm:gap-0 mt-3 text-[10px] sm:text-xs text-kin-text/70">
                <span>
                  Status:{' '}
                  <strong className={`capitalize ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
                    {isExpired ? 'Expired' : item?.status || 'Active'}
                  </strong>
                </span>
                <span>
                  Expires:{' '}
                  <strong className={isExpired ? 'text-red-600' : ''}>{expiredDateStr}</strong>
                </span>
              </div>
            </div>

            {/* Details */}
            <ul className="mt-4 text-xs sm:text-sm text-kin-text/80 space-y-2">
              <li className="flex items-center gap-2">
                <img
                  src="/kin/assest/image/icon-dollar.svg"
                  alt=""
                  className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0"
                />
                <span className="truncate">One-Time Payment</span>
              </li>
              <li className="flex items-center gap-2">
                <img
                  src="/kin/assest/image/icon-star.svg"
                  alt=""
                  className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0"
                />
                <span className="truncate">
                  Valid for any {findValidForClass(pkg?.type?.toLowerCase())}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <img
                  src="/kin/assest/image/icon-calender.svg"
                  alt=""
                  className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0"
                />
                <span className="truncate">
                  Valid for{' '}
                  <span className="capitalize">
                    {pkg?.validity_period_month === 0
                      ? 'Everyday'
                      : pkg?.validity_period_month
                      ? `${pkg?.validity_period_month} Month${
                          pkg.validity_period_month > 1 ? 's' : ''
                        }`
                      : 'Unlimited'}
                  </span>
                </span>
              </li>
            </ul>

            {pkg?.category === 'class' && (
              <div className="mt-4">
                {isExpired ? (
                  <PrimaryActionButton label="PASS EXPIRED" fullWidth disabled />
                ) : remainingQuota === 0 ? (
                  <PrimaryActionButton label="NO QUOTA LEFT" fullWidth disabled />
                ) : (
                  <PrimaryActionButton
                    label="USE PASSES"
                    fullWidth
                    onClick={() => goOurClass(pkg?.type)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CardContentPackage;
